import React, { useEffect } from 'react';

/**
 * Authorize.Net iframe communicator component
 * This handles communication between Authorize.Net's hosted payment form
 * and the parent checkout page
 */
const AuthNetCommunicator = () => {
    useEffect(() => {
        // Parse query string from hash or search params
        const parseQueryString = (str) => {
            const params = {};
            const pairs = str.replace(/^\?/, '').replace(/^#/, '').split('&');

            for (const pair of pairs) {
                if (pair) {
                    const [key, value] = pair.split('=');
                    if (key) {
                        params[decodeURIComponent(key)] = decodeURIComponent(value || '');
                    }
                }
            }
            return params;
        };

        // Function to send message to parent
        const sendMessageToParent = (data) => {
            // Try multiple targets for cross-frame communication
            const targets = [
                { target: window.parent, name: 'parent' },
                { target: window.parent?.parent, name: 'parent.parent' },
                { target: window.top, name: 'top' }
            ];

            let messageSent = false;

            for (const { target, name } of targets) {
                if (target && target !== window) {
                    try {
                        // Send to the parent's origin (React app)
                        target.postMessage(data, window.location.origin);
                        messageSent = true;
                    } catch (e) {
                        console.log(`🔧 Failed to send message to ${name}:`, e);
                    }
                }
            }

            if (!messageSent) {
                console.error('🔧 Could not send message to any parent window');
            }
        };

        // Handle hash-based communication (from URL)
        if (window.location.hash && window.location.hash.length > 1) {
            const hashData = parseQueryString(window.location.hash.substring(1));
            sendMessageToParent(hashData);
        }

        // Handle search param-based communication
        if (window.location.search && window.location.search.length > 1) {
            const searchData = parseQueryString(window.location.search.substring(1));
            sendMessageToParent(searchData);
        }

        // Listen for messages from Authorize.Net
        const handleMessage = (event) => {
            // Only process messages from Authorize.Net
            if (event.origin.includes('authorize.net')) {
                sendMessageToParent(event.data);
            }
        };

        // Add message listener
        window.addEventListener('message', handleMessage);

        // If no hash or search params, send a ping
        if (!window.location.hash && !window.location.search) {
            sendMessageToParent({ action: 'ping' });
        }

        // Cleanup
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            fontFamily: 'Arial, sans-serif',
            color: '#666',
            textAlign: 'center',
            padding: '20px'
        }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔒</div>
            <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>
                Secure Payment Communication
            </h3>
            <p style={{ margin: '0', fontSize: '0.875rem' }}>
                This page handles secure communication between the payment form and your checkout page.
            </p>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.75rem', opacity: 0.7 }}>
                If you see this page, the communication handler is working correctly.
            </p>
        </div>
    );
};

export default AuthNetCommunicator;